/** A submenu to the StandardMenu that _tries_ to be smart about which side,
 *  right or left, that it should render on.
 *
 *  Send it into a group of menu items like this:
 *    {
 *      label: 'do something',
 *      icon: <Icon size={IconSize.SMALL} />,
 *      disabled: isDisabled,
 *      submenu: (
 *        <StandardMenu
 *          ...
 *        />
 *      ),
 *      subMenuPreferredSide: 'left',
 *      estimatedSubmenuWidth: 200,
 *    }
 *
 *   Note: you currently can't use a label or scrollable={true}
 *   on the enclosing menu (or the submenu will appear inside
 *   the scrollable content)
 */ // TODO: the render chain is very snaky and should be simplified at some point
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import React, { useRef, useContext, useState, useCallback } from 'react';
import classnames from 'classnames';
import ArrowRightIcon from '@wix/da-ds/pkg/Icons/ArrowRight';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { DropdownStateContext } from '../../../DropdownStateManager';
import StandardMenuStyles from '../StandardMenuOption/StandardMenuOption.scss';
import s from './StandardMenuSubmenu.scss';
/** return how many pixels from the menuitem/open-button to the inner
 *  edges of the browser page */ function checkForRoom(node) {
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const bounds = node.getBoundingClientRect();
    return {
        left: bounds.left,
        right: viewportWidth - bounds.right,
        below: viewportHeight - bounds.bottom
    };
}
/** based on pref props, estimated sizes, and bounds get the best side
 *  to render the submenu on and whether we should render it upwards */ function pickASide(node, preferredSide, estimatedSubmenuWidth, minimumHeightNeeded) {
    const room = checkForRoom(node);
    let horizontal = room.left > room.right ? 'left' : 'right';
    if (preferredSide && room[preferredSide] >= estimatedSubmenuWidth) {
        horizontal = preferredSide;
    }
    return {
        horizontal,
        vertical: room.below < minimumHeightNeeded
    };
}
const StandardMenuSubmenu = ({ className, submenu, disabled, preferredSide = 'right', estimatedSubmenuWidth = 120, minimumHeightNeeded = 80, children })=>{
    const [submenuIsOpen, setSubmenuOpen] = useState(false);
    const [sideToRenderSubmenu, setSideToRenderSubmenu] = useState(preferredSide);
    const [renderUpwards, setRenderUpwards] = useState(false);
    const isMobile = useContext(MobileContext);
    const rootRef = useRef(null);
    const { closeDropdown } = useContext(DropdownStateContext);
    const openSubmenu = useCallback(()=>{
        if (!rootRef.current) {
            return;
        }
        const { horizontal, vertical } = pickASide(rootRef.current, preferredSide, estimatedSubmenuWidth, minimumHeightNeeded);
        setSubmenuOpen(true);
        setSideToRenderSubmenu(horizontal);
        setRenderUpwards(vertical);
    }, [
        estimatedSubmenuWidth,
        minimumHeightNeeded,
        preferredSide
    ]);
    const hideSubmenu = useCallback(()=>setSubmenuOpen(false), []);
    const toggleSubmenu = useCallback(()=>submenuIsOpen ? hideSubmenu() : openSubmenu(), [
        hideSubmenu,
        openSubmenu,
        submenuIsOpen
    ]);
    return /*#__PURE__*/ React.createElement("div", _object_spread({
        className: classnames(s['root'], disabled && StandardMenuStyles['disabled'], submenuIsOpen ? s['submenu-open'] : s['submenu-closed'], sideToRenderSubmenu === 'left' && s['submenu-left'], renderUpwards && s['submenu-upwards'], className),
        ref: rootRef,
        "aria-expanded": submenuIsOpen,
        "aria-disabled": disabled
    }, isMobile ? {
        onClick: toggleSubmenu
    } : {
        onMouseOver: openSubmenu,
        onMouseLeave: hideSubmenu,
        onClick: closeDropdown
    }), children, /*#__PURE__*/ React.createElement(ArrowRightIcon, {
        className: s['arrow'],
        size: IconSize.SMALL
    }), /*#__PURE__*/ React.createElement("div", {
        className: s['submenu']
    }, submenu));
};
StandardMenuSubmenu.displayName = 'StandardMenuSubmenu';
export default StandardMenuSubmenu;
